import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AMENITIES, YACHT_TYPES, api, errDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { ImageUploadButton, Thumb } from "@/pages/admin/shared";
import type { Yacht } from "@/types";

const EMPTY: Omit<Yacht, "id"> = {
  name: "", type: "motor", length_ft: 40, capacity: 10, cabins: 1, port: "Marina Cancún", description_es: "", description_en: "",
  amenities: [], photos: [], prices: { h4: 800, h6: 1100, h8: 1400, overnight: 2200 }, active: true, is_sample: false, order: 0,
  email_info: { checkin: "", checkout: "", services_es: "", services_en: "" },
};
const INFO_EMPTY = { checkin: "", checkout: "", services_es: "", services_en: "" };
const PRICE_FIELDS: [keyof Yacht["prices"], string][] = [["h4", "4 horas"], ["h6", "6 horas"], ["h8", "8 horas"], ["overnight", "Pernocta"]];

export default function YachtForm({ yacht, onSaved }: { yacht?: Yacht; onSaved: () => void }) {
  const { t } = useLang();
  const [f, setF] = useState<Omit<Yacht, "id">>(() => (yacht ? { ...yacht, email_info: { ...INFO_EMPTY, ...(yacht.email_info || {}) } } : { ...EMPTY }));
  const info = f.email_info || INFO_EMPTY;
  const setInfo = (k: keyof typeof INFO_EMPTY, v: string) => setF((p) => ({ ...p, email_info: { ...INFO_EMPTY, ...(p.email_info || {}), [k]: v } }));
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: any) => setF((p) => ({ ...p, [k]: v }));
  const num = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => set(k, Number(e.target.value));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, available, ...body } = f as any;
      if (yacht) await api.put(`/admin/yachts/${yacht.id}`, { ...body, is_sample: false });
      else await api.post("/admin/yachts", body);
      toast.success("Yate guardado");
      onSaved();
    } catch (err) {
      toast.error(errDetail(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="yacht-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2"><Label>Nombre</Label><Input required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="yacht-form-name" /></div>
        <div className="space-y-1.5"><Label>Tipo</Label>
          <Select value={f.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger data-testid="yacht-form-type"><SelectValue /></SelectTrigger>
            <SelectContent>{YACHT_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`type.${tp}`)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Puerto</Label><Input value={f.port} onChange={(e) => set("port", e.target.value)} data-testid="yacht-form-port" /></div>
        <div className="space-y-1.5"><Label>Eslora (pies)</Label><Input type="number" min={1} required value={f.length_ft} onChange={num("length_ft")} data-testid="yacht-form-length" /></div>
        <div className="space-y-1.5"><Label>Capacidad (personas)</Label><Input type="number" min={1} required value={f.capacity} onChange={num("capacity")} data-testid="yacht-form-capacity" /></div>
        <div className="space-y-1.5"><Label>Camarotes</Label><Input type="number" min={0} value={f.cabins} onChange={num("cabins")} data-testid="yacht-form-cabins" /></div>
        <div className="space-y-1.5"><Label>Orden en catálogo</Label><Input type="number" value={f.order} onChange={num("order")} data-testid="yacht-form-order" /></div>
      </div>
      <div>
        <Label>Precios por bloque (USD)</Label>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PRICE_FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1"><span className="text-xs text-slate-500">{label}</span>
              <Input type="number" min={0} required value={f.prices[k]} data-testid={`yacht-form-price-${k}`}
                onChange={(e) => set("prices", { ...f.prices, [k]: Number(e.target.value) })} /></div>
          ))}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Descripción (ES)</Label><Textarea rows={4} value={f.description_es} onChange={(e) => set("description_es", e.target.value)} data-testid="yacht-form-desc-es" /></div>
        <div className="space-y-1.5"><Label>Descripción (EN)</Label><Textarea rows={4} value={f.description_en} onChange={(e) => set("description_en", e.target.value)} data-testid="yacht-form-desc-en" /></div>
      </div>
      <div>
        <Label>Amenidades</Label>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {AMENITIES.map((a) => (
            <label key={a} className="flex items-center gap-2 text-sm">
              <Checkbox checked={f.amenities.includes(a)} data-testid={`yacht-form-amenity-${a}`}
                onCheckedChange={(c) => set("amenities", c ? [...f.amenities, a] : f.amenities.filter((x) => x !== a))} />
              {t(`am.${a}`)}
            </label>
          ))}
        </div>
      </div>
      <div>
        <Label>Fotos (la primera es la portada)</Label>
        <div className="mt-2 flex flex-wrap gap-3">
          {f.photos.map((p, i) => <Thumb key={p + i} src={p} testId={`yacht-form-remove-photo-${i}`} onRemove={() => set("photos", f.photos.filter((_, j) => j !== i))} />)}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <ImageUploadButton testId="yacht-form-upload" onUploaded={(u) => setF((p) => ({ ...p, photos: [...p.photos, u] }))} />
          <Input placeholder="o pega una URL de imagen" value={url} onChange={(e) => setUrl(e.target.value)} className="max-w-xs" data-testid="yacht-form-photo-url" />
          <button type="button" onClick={() => { if (url) { set("photos", [...f.photos, url]); setUrl(""); } }} className="text-sm font-semibold text-[#008C86]" data-testid="yacht-form-add-url">Agregar</button>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="yacht-form-email-info">
        <div><Label>Datos para el correo de confirmación</Label><p className="text-xs text-slate-500">Se incluyen automáticamente en el correo del cliente cuando reserva este yate. Vacío = usa los campos globales de la pestaña Correos.</p></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><span className="text-xs text-slate-500">Check in</span><Input placeholder="10:00 am" value={info.checkin} onChange={(e) => setInfo("checkin", e.target.value)} data-testid="yacht-form-checkin" /></div>
          <div className="space-y-1.5"><span className="text-xs text-slate-500">Check out</span><Input placeholder="4:00 pm" value={info.checkout} onChange={(e) => setInfo("checkout", e.target.value)} data-testid="yacht-form-checkout" /></div>
          <div className="space-y-1.5"><span className="text-xs text-slate-500">Servicios adicionales (ES)</span><Input value={info.services_es} onChange={(e) => setInfo("services_es", e.target.value)} data-testid="yacht-form-services-es" /></div>
          <div className="space-y-1.5"><span className="text-xs text-slate-500">Additional services (EN)</span><Input value={info.services_en} onChange={(e) => setInfo("services_en", e.target.value)} data-testid="yacht-form-services-en" /></div>
        </div>
      </div>
      <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="yacht-form-active" />Visible en el sitio</label>
      <button type="submit" disabled={saving} data-testid="yacht-form-save-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B192C] py-3 text-sm font-semibold text-white hover:bg-[#13294a] disabled:opacity-60">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}Guardar yate
      </button>
    </form>
  );
}
